<template>
    <Container padding="none" height="with-footer" class="pt-(--header-height) pb-12">
        <Head>
            <Title>{{ categoryProducts?.category?.name }}</Title>
            <Meta name="description" :content="categoryDescription" />
        </Head>
        <div class="py-4 max-lg:px-2">
            <CreateBreadcrumb :list="breadcrumbs" :max-items="5" />
        </div>
        <div v-if="subCategories.length" class="pt-8 pb-16">
            <PageCategoriesList :list="subCategories" :loading="pending" />
        </div>
        <div class="max-lg:px-2">
            <PageCategoriesFiltersMain v-model="activeFilters" :filters :loading />
            <div v-if="subCategories.length">
                <Separator />
                <WidgetSectionTitle :title="t('category.page.products')" />
                <Separator />
            </div>
            <div v-if="products" class="mt-12">
                <div v-if="pending" class="bg-primary/10 p-8 text-primary text-center">
                    <Loading />
                </div>
                <div v-else-if="products.length === 0" class="bg-primary/10 p-8 text-primary text-center">
                    {{ t("category.page.no_items_match") }}
                </div>
                <WidgetProductList v-else-if="products.length" :products :loading="pending" class="gap-2 lg:gap-8" />
            </div>
        </div>
    </Container>
</template>

<script lang="ts" setup>
import type { ActiveFilter } from "~/components/page/categories/filters/main.vue";
import { CreateBreadcrumb } from "~/components/ui/breadcrumb";
import type { BreadcrumbItem } from "~/components/ui/breadcrumb/CreateBreadcrumb.vue";
import { Separator } from "~/components/ui/separator";

const route = useRoute();
const { t } = useI18n();
const activeFilters = ref<ActiveFilter>();
const slug = route.params?.slug?.at(-1);

const { data: categoryProducts, pending, refresh } = useCategoryProducts(slug, ref({ filter: activeFilters }));
const { data: categories } = useCategories();
const { data, pending: loading } = useCategoryFilters(slug);

const breadcrumbs: ComputedRef<BreadcrumbItem[]> = computed(() => {
    return [
        {
            label: t("general.home"),
            to: "/",
            icon: {
                name: "majesticons:home",
            },
        },
        {
            label: t("general.categories"),
            to: "/categories",
            icon: {
                name: "majesticons:tickets-line",
            },
        },
        ...getCategoryBreadcrumb(categoryProducts.value?.category, "", t).reverse(),
    ];
});

watch(
    activeFilters,
    async () => {
        await refresh;
    },
    {
        immediate: true,
        deep: true,
    },
);

const subCategories = computed(() => useFilter(categories.value?.data, (e: APICategory) => e.parent?.slug === slug));

const products = computed(() => categoryProducts?.value?.products ?? []);

const rowFilters = shallowRef(data);

const filters = computed(() => {
    return isEmpty(rowFilters?.value) ? null : rowFilters?.value;
});

const categoryDescription = computed(() => {
    if (categoryProducts.value?.category?.seo?.description) return categoryProducts.value.category.seo.description;
    else if (categoryProducts.value?.category.name) return t("category.page.description_with_name", { name: categoryProducts.value.category.name });
    else return t("category.page.description");
});

// OG Image for Category page
const categoryTitle = computed(() => categoryProducts.value?.category?.seo?.title || categoryProducts.value?.category?.name || "Category");
const categoryIcon = computed(() => categoryProducts.value?.category?.icon || "");
defineOgImageComponent("Category", {
    title: categoryTitle.value,
    description: categoryDescription.value,
    icon: categoryIcon.value,
});
</script>

<style></style>
